import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  submit,
  openInspection,
  ensureOpenInspectionWorkSession,
  clearOpenInspectionLoadError,
  setOpenInspectionLoadError,
} from '../slices/openInspectionSlice';
import { PartialInspection } from '@/types/ModuleComponentProp.type';
import { RootState } from '../rootReducer';
import { AppDispatch } from '../store';
import { getDefectLanguage } from '@/utils/inspection.utils';
import { savedInspectionsStorageV2 } from '@/store/idbStorage/savedInspectionsStorageV2';
import { upsertInspection } from '@/store/slices/inspectionsSlice';
import { Inspection } from '@/types/Inspection.type';
import { startUpload } from '@/serviceworker/mediator';
import { calculateInspectionResult } from '@/utils/inspectionResult.utils';
import InspectionStatus from '@/enums/InspectionStatus.enum';
import { navigateToNotFound } from '@/utils/navigation.utils';
import { InspectionOwner } from '@/enums/InspectionOwner.enum';
import { DefectCode, DefectSeverity } from '@/types/DefectCategory.type';
import { getCurrentPosition } from '@/utils/geolocation';
import { masterDataIdbService } from '@/store/idbStorage/masterDataIdbService';
import { setDefectCodesAndSeverities } from '@/store/slices/masterDataSlice';
import { stampInspectionEndTime } from '@/utils/inspectionEndTime.utils';
import { closeOpenInspectionWorkSession } from '@/utils/inspectionWorkSession.utils';
import {
  loadInspectionForInspectApp,
  resolveInspectionForOpen,
} from '@/utils/ensureInspectionHydrated';
import { stampSubmittingInspector } from '@/utils/inspectionSubmitInspector.utils';
import { hasRole } from '@/utils/roleUtils';
import { ROLE } from '@/role.constant';

export const openInspectionById = (id: string) => async (dispatch: AppDispatch, getState: any) => {
  try {
    const state = getState() as RootState;
    const currentUser = state.auth.user;
    const isAdministrator = hasRole(currentUser?.roles ?? [], ROLE.ADMINISTRATOR);
    if (state.openedInspection.inspection?.id === id) {
      dispatch(
        ensureOpenInspectionWorkSession({
          userId: currentUser?.id ?? '',
          isAdministrator,
        })
      );
      return;
    }
    dispatch(clearOpenInspectionLoadError());
    const savedInspection = await savedInspectionsStorageV2.get(id);
    const fromList = state.inspectionsData?.inspections.find((i: Inspection) => i.id === id);
    if (!savedInspection && !fromList) {
      console.error(`Inspection with ID ${id} not found`);
      navigateToNotFound();
      return;
    }
    const loadedFromCache = resolveInspectionForOpen({
      savedInspection: savedInspection ?? null,
      listRow: fromList ?? null,
    });
    const loaded =
      !loadedFromCache.ok && loadedFromCache.reason === 'offline_lite' && navigator.onLine
        ? await loadInspectionForInspectApp({
            inspectionId: id,
            isOnline: true,
            savedInspection: null,
            listRow: fromList ?? null,
            dispatch,
          })
        : loadedFromCache;
    if (!loaded.ok) {
      if (loaded.reason === 'not_found') {
        navigateToNotFound();
      } else {
        dispatch(
          setOpenInspectionLoadError({
            inspectionId: id,
            reason: loaded.reason === 'offline_lite' ? 'offline_lite' : 'fetch_failed',
          })
        );
      }
      return;
    }
    let inspection: Inspection = structuredClone(loaded.inspection);

    // Get required data from state, fallback to IndexedDB if needed
    let defectCodes = state.masterData?.defectsByDivision.flatMap((d) => d.defects) || [];
    let defectSeverities = state.masterData?.severities || [];

    if (!defectCodes.length || !defectSeverities.length) {
      const cachedSeverities = await masterDataIdbService.getSeverities();
      const cachedControlPointId = cachedSeverities?.controlPointId;
      const cachedDivisions = cachedControlPointId
        ? await masterDataIdbService.getAllDefectDivisions(cachedControlPointId)
        : [];

      if (cachedSeverities && cachedDivisions.length) {
        dispatch(
          setDefectCodesAndSeverities({
            defectsByDivision: cachedDivisions as any,
            severities: cachedSeverities.severities,
            controlPointId: cachedControlPointId,
          })
        );
        defectCodes = cachedDivisions.flatMap((d) => d.defects) || [];
        defectSeverities = cachedSeverities.severities || [];
      }
    }

    // Process defects if they exist and required data is available
    if (inspection.qcDefects && defectCodes && defectSeverities) {
      for (const defect of inspection.qcDefects) {
        // Find and assign defect code
        for (const category of defectCodes) {
          const code = category.codes.find((c: DefectCode) => c.id == defect.defectId);
          if (code) {
            defect.defectCode = structuredClone(code);
            if (defect.defectCode) {
              const language = getDefectLanguage(code);
              defect.defectCode.categoryDescription = language?.categoryDescription ?? '';
              defect.defectCode.codeDescription = language?.codeDescription?.trim()
                ? language.codeDescription
                : code.code;
            }
            break; // Exit loop once code is found
          }
        }

        // Find and assign defect severity
        const severity = defectSeverities.find(
          (s: DefectSeverity) => s.id == defect.defectSeverityId
        );
        if (severity) {
          defect.defectSeverity = severity.name;
        }
      }
    } else {
      console.warn('Missing defect data: defects, codes, or severities not available');
    }

    dispatch(upsertInspection(inspection));

    // Open the inspection
    dispatch(openInspection(inspection));
    const userAfterOpen = getState().auth.user;
    dispatch(
      ensureOpenInspectionWorkSession({
        userId: userAfterOpen?.id ?? '',
        isAdministrator: hasRole(userAfterOpen?.roles ?? [], ROLE.ADMINISTRATOR),
      })
    );
  } catch (error) {
    console.error('Error opening inspection:', error);
  }
};

export const submitOpenedInspection = createAsyncThunk(
  'inspection/updateOpenedInspection',
  async (inspectionUpdate: PartialInspection, { dispatch, getState }) => {
    const state = getState() as RootState;
    const openedInspection = state.openedInspection.inspection;

    if (openedInspection) {
      const inspectionFieldsToApply = { ...inspectionUpdate };
      delete inspectionFieldsToApply.startTime;
      const currentUser = state.auth.user;
      const updatedInspection = stampSubmittingInspector({
        inspection: { ...openedInspection, ...inspectionFieldsToApply },
        user: currentUser,
        isAdministrator: hasRole(currentUser?.roles ?? [], ROLE.ADMINISTRATOR),
      });

      const result = calculateInspectionResult(updatedInspection, state.masterData);
      const autoClose = state.appConfig.qcInspection?.autoCloseInspection || false;
      if (autoClose) {
        updatedInspection.inspectionStatusId = InspectionStatus.CLOSED;
      } else {
        updatedInspection.inspectionStatusId = InspectionStatus.FINISHED;
      }
      updatedInspection.result = result;
      await saveInspectionInPersitStore(updatedInspection);
      dispatch(submit(updatedInspection));
    } else {
      console.error('No inspection opened for update');
    }
  }
);
const saveInspectionInPersitStore = async (inspection: Inspection) => {
  let submitCoordinates: { latitude: number; longitude: number } | undefined;
  if ('geolocation' in navigator) {
    try {
      const position = await getCurrentPosition({
        timeout: 5000,
        maximumAge: 60000,
      });
      const { latitude, longitude } = position.coords;
      inspection.latitude = latitude;
      inspection.longitude = longitude;
      submitCoordinates = { latitude, longitude };
      console.log('Geolocation obtained successfully:', { latitude, longitude });
    } catch (error: any) {
      console.error('Geolocation error:', error.message);
      // Set default coordinates or leave as undefined if geolocation fails
      // Don't block the submission process
      if (error.code === 1) {
        console.warn('Geolocation permission denied');
      } else if (error.code === 2) {
        console.warn('Geolocation position unavailable');
      } else if (error.code === 3) {
        console.warn('Geolocation timeout');
      }
      // Continue without geolocation data
    }
  } else {
    console.warn('Geolocation is not supported by this browser.');
  }

  const endIso = new Date().toISOString();
  try {
    inspection.workSessions = closeOpenInspectionWorkSession(
      inspection.workSessions,
      endIso,
      submitCoordinates
    );
  } catch (error) {
    console.warn('Failed to close inspection work session before submit', error);
  }
  stampInspectionEndTime(inspection, endIso);
  try {
    await savedInspectionsStorageV2.save(inspection, InspectionOwner.UI);
  } catch (error) {
    console.error('Error saving inspection:', error);
  }
  // Send message to service worker after saving
  try {
    startUpload([inspection.id]);
  } catch (error) {
    console.error('Error starting upload:', error);
  }
};
